// Panel unificado de Ventas — ML + Limpack en una sola lista (look mockup panel-ventas-unificado).
// v3 (2026-09-02): tira de contexto (corte/ruta), KPIs clickeables con subtexto, tabs por momento,
// multi-seleccion + acciones masivas (Facturar seleccionadas / Descargar CFEs), columnas con encabezado,
// color por canal (ML amarillo, Limpack azul), chips de SKU, y por fila: Mensaje al comprador, Etiqueta,
// Vista previa (modal dry_run con Emitir/Forzar 22%), Facturar y Adjuntar a ML. Limpack se factura solo.
// Fuente: pedidos (Limpack) + ml_ventas (ML) + facturas_emitidas (CFE) + entregas (envío).

import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const num = (n) => Number(n || 0)
const fmt = (n) => '$ ' + num(n).toLocaleString('es-UY', { maximumFractionDigits: 0 })
const fmt2 = (n) => '$ ' + num(n).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoyStr = () => new Date().toDateString()
function fechaCorta(d) {
  if (!d) return ''
  const x = new Date(d)
  const hh = x.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
  if (x.toDateString() === hoyStr()) return 'Hoy ' + hh
  return x.toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) + ' ' + hh
}
function ivaLabel(ind) {
  const i = Number(ind)
  if (i === 2) return '10%'; if (i === 3) return '22%'; if (i === 1) return 'Exento'; if (i === 5) return 'Gratuita'
  return '22%'
}
function desgloseNetoIva(lineas) {
  let neto = 0, iva = 0
  for (const l of (lineas || [])) {
    const total = l.subtotal_linea != null ? Number(l.subtotal_linea) : Number(l.precio) * Number(l.cantidad || 1)
    const ind = Number(l.indicador_facturacion)
    const rate = ind === 2 ? 10 : ind === 3 ? 22 : 0
    const n = rate > 0 ? total / (1 + rate / 100) : total
    neto += n; iva += (total - n)
  }
  return { neto, iva }
}
function corteLimpackFlex() {
  const now = new Date()
  const corte = new Date(now); corte.setHours(16, 0, 0, 0)
  const diff = corte - now
  if (diff <= 0) return { txt: 'Corte LimpackFlex 16:00 · cerrado por hoy', hot: false }
  const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000)
  return { txt: `Corte LimpackFlex 16:00 · faltan ${h} h ${String(m).padStart(2, '0')}`, hot: h < 3 }
}

// Liberación de fondos MP: fecha en que el dinero cobrado pasa de "retenido" a disponible en la caja de MP.
// fecha_liberacion_real = confirmada por MP · fecha_liberacion_estimada = money_release_date del payload (aprox).
function libDateCorta(s) {
  if (!s) return ''
  const p = String(s).slice(0, 10).split('-')
  return `${p[2]}/${p[1]}`
}
function libDateLarga(s) {
  if (!s) return ''
  return new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('es-UY', { day: '2-digit', month: 'long' })
}
function libInfo(reg) {
  if (!reg) return null
  const real = reg.fecha_liberacion_real, est = reg.fecha_liberacion_estimada
  if (!real && !est) return null
  const monto = Number(reg.monto_neto || 0)
  const key = reg.mp_payment_id || reg.external_reference || null   // para deduplicar packs con varias líneas
  if (real) return { estado: 'liberado', fecha: real, monto, key, aprox: false }
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  const d = new Date(est + 'T00:00:00')
  if (d > hoy) return { estado: 'retenido', fecha: est, monto, key, aprox: false }
  return { estado: 'liberado', fecha: est, monto, key, aprox: true }   // estimada ya pasó, MP aún no confirmó la real
}

const ML_SHIP = {
  ready_to_ship: 'Listo para enviar', pending: 'Pendiente', handling: 'En preparación',
  shipped: 'En camino', delivered: 'Entregado', not_delivered: 'No entregado', cancelled: 'Cancelado',
}
// Cubre AMBOS enums: pedido_estado (masculino: entregado/cancelado) y entrega_estado (femenino: entregada/cancelada/no_entregada).
const PEDIDO_ESTADO = {
  pendiente_pago: { t: 'Espera pago', c: 'warn' }, pago_en_revision: { t: 'Pago en revisión', c: 'warn' },
  programada: { t: 'Programada', c: 'sky' },
  en_armado: { t: 'En armado', c: 'warn' }, en_ruta: { t: 'En ruta', c: 'sky' },
  entregado: { t: 'Entregado', c: 'ok' }, entregada: { t: 'Entregado', c: 'ok' },
  no_entregada: { t: 'No entregado', c: 'danger' },
  cancelado: { t: 'Cancelado', c: 'grey' }, cancelada: { t: 'Cancelado', c: 'grey' },
  cancelada_pago_fallido: { t: 'Cancelado (pago)', c: 'grey' }, cancelada_por_pausa: { t: 'Cancelado (pausa)', c: 'grey' },
  rechazado: { t: 'Rechazado', c: 'danger' },
}
const PEDIDO_TIPO = { compra_unica: 'Compra única', productos_sueltos: 'Productos sueltos', suscripcion: 'Suscripción', b2b: 'B2B' }

// Bloque "Reparto" del detalle: a quién se le asigna la entrega (repartidor) + dirección de envío.
// Usa el RPC ml_asignar_repartidor (mismo que la pantalla ML anterior): crea/linkea la entrega.
function RepartoBlock({ row, reps, asignando, onAsignar }) {
  const [repId, setRepId] = useState(row.repartidorId || '')
  const [dir, setDir] = useState(row.entregaDirManual || row.direccion || '')
  const [lat, setLat] = useState(row.entregaLat ?? '')
  const [lng, setLng] = useState(row.entregaLng ?? '')
  const [trayendo, setTrayendo] = useState(false)
  useEffect(() => {
    setRepId(row.repartidorId || ''); setDir(row.entregaDirManual || row.direccion || '')
    setLat(row.entregaLat ?? ''); setLng(row.entregaLng ?? '')
  }, [row.id])  // eslint-disable-line react-hooks/exhaustive-deps
  async function traerDir() {
    setTrayendo(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-sync-shipment`, {
        method: 'POST', headers: { Authorization: `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ml_venta_id: row.id }),
      })
      const j = await r.json().catch(() => ({}))
      if (r.ok && j.ok) { if (j.direccion) setDir(j.direccion); if (j.lat != null) setLat(j.lat); if (j.lng != null) setLng(j.lng); if (!j.direccion) alert('ML no devolvió dirección para este envío.') }
      else alert('No se pudo traer la dirección de ML: ' + (j.error || r.status))
    } catch (e) { alert('Error trayendo dirección: ' + e.message) } finally { setTrayendo(false) }
  }
  const dirty = repId !== (row.repartidorId || '') || dir !== (row.entregaDirManual || row.direccion || '') || String(lat) !== String(row.entregaLat ?? '') || String(lng) !== String(row.entregaLng ?? '')
  return (
    <div className="vu-det-reparto">
      <div className="vu-det-reparto-h">🚚 Reparto — ¿quién lo lleva?{row.repartidorNombre && <span className="vu-rep-actual">actual: {row.repartidorNombre}</span>}</div>
      <div className="vu-rep-row">
        <select value={repId} onChange={e => setRepId(e.target.value)}>
          <option value="">— elegir repartidor —</option>
          {reps.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <button type="button" className="vu-btn ghost sm" onClick={traerDir} disabled={trayendo}>{trayendo ? 'Trayendo…' : '📍 Dirección de envío (ML)'}</button>
      </div>
      <input className="vu-rep-input" value={dir} onChange={e => setDir(e.target.value)} placeholder="Dirección de envío (Cavia 2981, Pocitos)" />
      <button type="button" className="vu-btn pri sm" disabled={!dirty || !repId || asignando} onClick={() => onAsignar(row, repId, lat, lng, dir)}>
        {asignando ? 'Asignando…' : (row.repartidorId ? 'Actualizar reparto' : 'Asignar reparto')}
      </button>
    </div>
  )
}

export default function VentasUnificado() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [vista, setVista] = useState('todas')
  const [canal, setCanal] = useState('todos')
  const [q, setQ] = useState('')
  const [emitiendo, setEmitiendo] = useState(null)
  const [adjuntando, setAdjuntando] = useState(null)
  const [ivaError, setIvaError] = useState({})
  const [sel, setSel] = useState(() => new Set())
  const [expanded, setExpanded] = useState(null)   // row.key con el detalle abierto
  const [detail, setDetail] = useState({})         // key -> preview dry_run
  const [detailLoad, setDetailLoad] = useState(null)
  const [ruta, setRuta] = useState({ barrios: [], repart: [], n: 0 })
  const [posiblesReps, setPosiblesReps] = useState([])
  const [asignandoRep, setAsignandoRep] = useState(null)
  const [limite, setLimite] = useState(120)   // carga inicial liviana; "cargar más" lo sube

  const cargar = useCallback(async () => {
    setLoading(true)
    const [mlR, pedR, facR, entR, prodR, profR, petR, payR] = await Promise.all([
      supabase.from('ml_ventas')
        .select('id, ml_order_id, ml_pack_id, ml_order_ids, ml_item_id, producto_id, cantidad, cliente_nombre, cliente_nickname, cliente_telefono, cliente_direccion, monto_bruto, comision_monto, costo_fijo, neto, compensacion_envio_gratis, neto_real, status, es_flex, shipping_mode, shipping_status, envio_gross, cfe_id, cfe_pdf_url, ml_factura_adjuntada_at, date_created, repartidor_id, entrega_lat, entrega_lng, entrega_direccion_manual, ml_order_items:order_items')
        .order('date_created', { ascending: false }).limit(limite),
      supabase.from('pedidos')
        .select('id, numero_orden, tipo, empresa_id, kit_codigo, items, comprador_nombre, comprador_telefono, comprador_documento, entrega_direccion, monto_total, costo_envio, estado, pagado_at, mp_payment_id, emitir_cfe, entrega_id, fecha_entrega_programada, created_at, es_prueba')
        .order('created_at', { ascending: false }).limit(limite),
      supabase.from('facturas_emitidas').select('id, serie, numero, estado, tipo_cfe, pdf_path, pedido_id, ml_venta_id'),
      supabase.from('entregas').select('id, estado, fecha_programada, ts_entregada, canal, barrio_snapshot, repartidor_id, orden_ruta'),
      supabase.from('productos').select('id, sku, nombre, iva_pct'),
      supabase.from('profiles').select('id, nombre').in('role', ['admin', 'superadmin', 'repartidor']),
      supabase.rpc('petpack_ventas', { p_limit: limite }),   // canal PetPack (schema aislado, read-only)
      supabase.from('mp_pagos_registro')   // liberación de fondos MP (tienda + ML): retenido vs liberado
        .select('mp_payment_id, external_reference, canal, fecha_liberacion_estimada, fecha_liberacion_real, monto_neto')
        .limit(300),
    ])
    const prodMap = {}, skuIva = {}
    for (const p of (prodR.data || [])) { prodMap[p.id] = p; if (p.sku) skuIva[p.sku] = p.iva_pct }
    const repMap = {}; for (const p of (profR?.data || [])) repMap[p.id] = p.nombre
    setPosiblesReps(profR?.data || [])
    // Ruta de hoy: barrios (en orden de ruta) + repartidores de las entregas programadas para hoy.
    const d = new Date(); const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const entHoy = (entR.data || []).filter(e => e.fecha_programada === ymd && e.estado !== 'cancelada')
      .sort((a, b) => (a.orden_ruta ?? 99) - (b.orden_ruta ?? 99))
    setRuta({
      barrios: [...new Set(entHoy.map(e => e.barrio_snapshot).filter(Boolean))],
      repart: [...new Set(entHoy.map(e => repMap[e.repartidor_id]).filter(Boolean))],
      n: entHoy.length,
    })
    const facByPedido = {}, facByMl = {}
    for (const f of (facR.data || [])) { if (f.pedido_id) facByPedido[f.pedido_id] = f; if (f.ml_venta_id) facByMl[f.ml_venta_id] = f }
    const entById = {}; for (const e of (entR.data || [])) entById[e.id] = e
    // Liberación MP: index por mp_payment_id (tienda) y por external_reference = pack/order id (ML).
    const libByPay = {}, libByRef = {}
    for (const r of (payR.data || [])) {
      if (r.mp_payment_id) libByPay[r.mp_payment_id] = r
      if (r.external_reference) libByRef[r.external_reference] = r
    }

    const unif = []
    for (const v of (mlR.data || [])) {
      const fac = facByMl[v.id] || null
      const prod = prodMap[v.producto_id]
      const oi = Array.isArray(v.ml_order_items) ? v.ml_order_items : []
      const oiLineas = oi.map(it => {
        const sku = it.item?.seller_sku || it.item?.seller_custom_field || null
        return { concepto: it.item?.title || 'Producto', cantidad: it.quantity || 1, importe: num(it.unit_price) * (it.quantity || 1), sku, ml_item_id: it.item?.id || null, iva_pct: (sku != null && skuIva[sku] != null) ? skuIva[sku] : (prod?.iva_pct ?? null) }
      })
      const _oiSum = oiLineas.reduce((s, l) => s + l.importe, 0)
      if (oiLineas.length && Math.abs(_oiSum - num(v.monto_bruto)) > 1) {
        oiLineas.push({ concepto: 'Otros ítems del paquete', cantidad: 1, importe: num(v.monto_bruto) - _oiSum, sku: null, ml_item_id: null, iva_pct: null })
      }
      const cancelada = v.status === 'cancelled'
      const shipped = v.shipping_status === 'shipped'
      const delivered = v.shipping_status === 'delivered'
      // "Por salir hoy" solo si el envio esta realmente pendiente de despacho, o la venta se creo hoy.
      // Sin esto, una venta paga con shipping_status desconocido (null) quedaba en "Para hoy" para siempre.
      const preShip = ['ready_to_ship', 'handling', 'pending', 'ready_to_print'].includes(v.shipping_status)
      const creadaHoy = new Date(v.date_created).toDateString() === hoyStr()
      const tieneCfe = !!(v.cfe_id || fac)
      const adjuntada = !!v.ml_factura_adjuntada_at
      const facturable = v.status === 'paid' && !tieneCfe
      const esFlexRow = v.es_flex === true       // Flex: lo entregamos nosotros
      const envioDesc = v.es_flex == null         // sin shipping_id / sin clasificar
      const cerradaNuestroLado = v.status === 'paid' && tieneCfe && adjuntada  // cobrada + facturada + adjuntada
      const accionPendML = facturable || (tieneCfe && !adjuntada && !cancelada)  // ML se factura a mano: falta emitir o adjuntar
      const regML = libByRef[v.ml_pack_id] || libByRef[v.ml_order_id] || ((v.ml_order_ids || []).map(x => libByRef[x]).find(Boolean)) || null
      unif.push({
        key: 'ml_' + v.id, id: v.id, canal: 'ml', esB2b: false, esFlex: v.es_flex === true, envioDesconocido: v.es_flex == null,
        lib: libInfo(regML),
        tipo: v.es_flex === true ? 'Flex' : (v.es_flex === false ? 'Agencia' : 'Envío ML'),
        numero: '#' + (v.ml_order_id || v.id.slice(0, 8)),
        fecha: v.date_created,
        clienteNombre: v.cliente_nombre || v.cliente_nickname || 'Comprador ML',
        clienteSub: v.cliente_nickname || '', clienteDoc: '', telefono: v.cliente_telefono || '',
        items: oiLineas.length ? oiLineas[0].concepto : (prod ? prod.nombre : `${v.cantidad || 1} unidad(es)`),
        itemsSub: oiLineas.length > 1 ? `${oiLineas.length} productos` : `${oiLineas[0]?.cantidad || v.cantidad || 1} u.`,
        skus: oiLineas.length ? [...new Set(oiLineas.map(l => l.sku).filter(Boolean))].slice(0, 4) : (prod?.sku ? [prod.sku] : []),
        lineasLocal: oiLineas.length ? oiLineas : [{ concepto: prod ? prod.nombre : (`Ítem ML ${v.ml_item_id || ''}`).trim(), cantidad: v.cantidad || 1, importe: num(v.monto_bruto), sku: prod?.sku || null, ml_item_id: v.ml_item_id || null, iva_pct: prod?.iva_pct ?? null }],
        monto: num(v.monto_bruto), envio: num(v.envio_gross),
        envioEstado: ML_SHIP[v.shipping_status] || (v.es_flex === true ? 'Flex' : (v.es_flex === false ? 'Agencia' : 'Envío ML')),
        envioClass: delivered ? 'ok' : (cancelada ? 'grey' : 'sky'),
        cobroLabel: cancelada ? 'Cancelada' : 'Cobrado por ML',
        cobroSub: v.neto_real != null ? 'Neto ' + fmt(v.neto_real) : '',
        cobroClass: cancelada ? 'grey' : 'ok',
        direccion: v.cliente_direccion || '', comision: num(v.comision_monto), costoFijo: num(v.costo_fijo), netoProducto: num(v.neto), creditoEnvio: num(v.compensacion_envio_gratis), netoReal: num(v.neto_real), packId: v.ml_pack_id,
        repartidorId: v.repartidor_id || '', repartidorNombre: v.repartidor_id ? (repMap[v.repartidor_id] || null) : null, entregaLat: v.entrega_lat, entregaLng: v.entrega_lng, entregaDirManual: v.entrega_direccion_manual || null,
        cancelada, factura: fac, cfeId: v.cfe_id, cfePdf: v.cfe_pdf_url, tieneCfe, adjuntada, facturable, pagado: true,
        enCamino: shipped && !delivered,
        // Finalizada: cancelada, o entregada SIN accion nuestra pendiente, o (Agencia/envio no visto) cerrada de nuestro
        // lado (cobrada+facturada+adjuntada) aunque ML no nos pase el delivered. Si falta facturar/adjuntar NO cierra:
        // el eje facturacion es manual y sigue pendiente aunque ya se haya entregado.
        finalizada: cancelada || (delivered && !accionPendML) || (!esFlexRow && cerradaNuestroLado),
        accionPendiente: accionPendML,
        // "Por salir hoy" = lo que despachamos nosotros: Flex pendiente de salir o creado hoy.
        // Agencia NO (la lleva el correo de ML). Envio sin clasificar de hoy se muestra por las dudas.
        hoy: !cancelada && !delivered && !shipped && ((esFlexRow && (preShip || creadaHoy)) || (envioDesc && creadaHoy && v.status === 'paid')),
      })
    }
    for (const p of (pedR.data || [])) {
      if (p.es_prueba) continue
      const fac = facByPedido[p.id] || null
      const ent = p.entrega_id ? entById[p.entrega_id] : null
      const est = PEDIDO_ESTADO[p.estado] || { t: p.estado, c: 'grey' }
      const estEnt = ent ? (PEDIDO_ESTADO[ent.estado] || { t: ent.estado, c: 'grey' }) : est
      const itemsArr = Array.isArray(p.items) ? p.items : []
      const primer = itemsArr[0]
      const nombrePrimer = primer ? (primer.nombre || primer.descripcion || primer.sku || 'Producto') : (p.kit_codigo ? 'Kit ' + p.kit_codigo : 'Pedido')
      // La entrega manda cuando existe. entrega_estado usa femenino (entregada/cancelada), pedido_estado masculino (entregado/cancelado).
      const estadoOps = ent?.estado || p.estado
      const cancelada = ['cancelado', 'rechazado', 'cancelada', 'cancelada_pago_fallido', 'cancelada_por_pausa'].includes(estadoOps)
      const enRuta = estadoOps === 'en_ruta'
      const entregado = estadoOps === 'entregado' || estadoOps === 'entregada'
      const pagado = !!p.pagado_at
      const regLP = p.mp_payment_id ? libByPay[p.mp_payment_id] : null
      unif.push({
        key: 'lp_' + p.id, id: p.id, canal: 'lp', esB2b: !!p.empresa_id, lib: libInfo(regLP),
        tipo: p.empresa_id ? 'B2B' : (PEDIDO_TIPO[p.tipo] || p.tipo || 'Limpack'),
        numero: p.numero_orden || p.id.slice(0, 8),
        fecha: p.created_at,
        clienteNombre: p.comprador_nombre || 'Cliente', clienteSub: '', clienteDoc: p.comprador_documento || '', telefono: p.comprador_telefono || '',
        items: nombrePrimer,
        itemsSub: itemsArr.length > 1 ? `${itemsArr.length} productos` : (p.kit_codigo ? 'Kit + regalo sorpresa' : '1 producto'),
        skus: [...new Set(itemsArr.map(it => it?.sku).filter(Boolean))].slice(0, 4),
        lineasLocal: (itemsArr.length ? itemsArr : [{ nombre: nombrePrimer }]).map(it => ({ concepto: it.nombre || it.descripcion || it.sku || 'Producto', cantidad: it.cantidad || 1, importe: num(it.subtotal ?? it.importe ?? ((it.precio_unit ?? it.precio) != null ? (it.precio_unit ?? it.precio) * (it.cantidad || 1) : 0)), sku: it.sku || null, ml_item_id: null, iva_pct: it.iva_pct ?? (it.sku != null ? skuIva[it.sku] : null) ?? null })),
        monto: num(p.monto_total), envio: num(p.costo_envio),
        envioEstado: estEnt.t, envioClass: estEnt.c,
        cobroLabel: pagado ? 'Cobrado' : 'Pendiente', cobroSub: pagado ? '' : (p.empresa_id ? 'B2B a plazo' : ''),
        cobroClass: pagado ? 'ok' : 'warn',
        direccion: p.entrega_direccion || '', comision: 0, costoFijo: 0, netoProducto: 0, creditoEnvio: 0, netoReal: 0, packId: null,
        cancelada, factura: fac, cfeId: fac ? fac.id : null, cfePdf: null, tieneCfe: !!fac, adjuntada: false,
        facturable: false, noFacturaCorresponde: p.emitir_cfe === false, pagado,
        enCamino: enRuta, finalizada: cancelada || entregado,
        accionPendiente: !pagado && !cancelada,
        hoy: !cancelada && !entregado && ((new Date(p.created_at).toDateString() === hoyStr()) || (p.fecha_entrega_programada && new Date(p.fecha_entrega_programada).toDateString() === hoyStr())),
      })
    }
    // PetPack (canal mascotas) — pedidos del schema aislado petpack, vía RPC read-only.
    // Entran como canal 'lp' (misma lógica de pedido propio: cobro, entrega, WhatsApp) pero con
    // flag esPet → tag VERDE "PetPack" + filtro propio. Facturación pet: pendiente (no CFE aún).
    for (const p of (Array.isArray(petR?.data) ? petR.data : [])) {
      const itemsArr = Array.isArray(p.items) ? p.items : []
      const primer = itemsArr[0]
      const nombrePrimer = primer ? (primer.nombre || primer.descripcion || primer.sku || 'Producto') : 'Pedido'
      const estadoOps = p.estado
      const cancelada = ['cancelado', 'cancelada'].includes(estadoOps)
      const entregado = estadoOps === 'entregado'
      const enRuta = estadoOps === 'en_camino'
      const pagado = !['pendiente_pago', 'cancelado'].includes(estadoOps)
      const est = PEDIDO_ESTADO[estadoOps] || { t: estadoOps, c: 'grey' }
      unif.push({
        key: 'petpack_' + p.id, id: p.id, canal: 'lp', esPet: true, esB2b: false,
        tipo: p.tipo === 'programado' ? 'Programado' : 'PetPack',
        numero: p.numero_orden || String(p.id).slice(0, 8),
        fecha: p.created_at,
        clienteNombre: p.comprador_nombre || 'Cliente', clienteSub: '', clienteDoc: p.comprador_documento || '', telefono: p.comprador_telefono || '',
        items: nombrePrimer,
        itemsSub: itemsArr.length > 1 ? `${itemsArr.length} productos` : '1 producto',
        skus: [...new Set(itemsArr.map(it => it?.sku).filter(Boolean))].slice(0, 4),
        lineasLocal: (itemsArr.length ? itemsArr : [{ nombre: nombrePrimer }]).map(it => ({ concepto: it.nombre || it.descripcion || it.sku || 'Producto', cantidad: it.cantidad || 1, importe: num(it.subtotal ?? it.importe ?? (it.precio_unit != null ? it.precio_unit * (it.cantidad || 1) : 0)), sku: it.sku || null, ml_item_id: null, iva_pct: null })),
        monto: num(p.monto_total), envio: num(p.costo_envio),
        envioEstado: est.t, envioClass: est.c,
        cobroLabel: pagado ? 'Cobrado' : 'Pendiente', cobroSub: '',
        cobroClass: pagado ? 'ok' : 'warn',
        direccion: p.entrega_direccion || '', comision: 0, costoFijo: 0, netoProducto: 0, creditoEnvio: 0, netoReal: 0, packId: null,
        cancelada, factura: null, cfeId: null, cfePdf: null, tieneCfe: false, adjuntada: false,
        facturable: false, noFacturaCorresponde: true, pagado,
        enCamino: enRuta, finalizada: cancelada || entregado,
        accionPendiente: !pagado && !cancelada,
        hoy: !cancelada && !entregado && ((new Date(p.created_at).toDateString() === hoyStr()) || (p.fecha_entrega_programada && new Date(p.fecha_entrega_programada).toDateString() === hoyStr())),
      })
    }
    unif.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
    setRows(unif)
    setSel(new Set())
    setLoading(false)
  }, [limite])

  useEffect(() => { cargar() }, [cargar])

  async function emitir(row, forzar) {
    const { data: { session } } = await supabase.auth.getSession()
    const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-cfe`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ml_venta_id: row.id, ...(forzar ? { forzar_iva: true } : {}) }),
    })
    const j = await r.json().catch(() => ({}))
    return { ok: r.ok && j.ok, status: r.status, j }
  }

  async function facturarML(row, forzar = false, adjuntarDespues = false) {
    if (!row || emitiendo) return
    const extra = forzar ? '\n\n⚠️ Vas a FORZAR IVA 22% en todas las líneas. Solo si el producto realmente es 22%.' : ''
    const accion = adjuntarDespues ? 'Emitir el e-Ticket y adjuntarlo a ML' : 'Emitir el e-Ticket'
    if (!confirm(`${accion} de la venta ${row.numero}?\nSe emite en DGI vía Biller (no se puede deshacer).${extra}`)) return
    const pdfWin = window.open('', '_blank')
    if (pdfWin) { try { pdfWin.document.write('<p style="font-family:sans-serif;padding:24px;color:#334155">Emitiendo la factura…</p>') } catch (_) {} }
    setEmitiendo(row.key)
    try {
      const { ok, status, j } = await emitir(row, forzar)
      if (ok) {
        setIvaError(e => ({ ...e, [row.key]: false }))
        const pdfUrl = j.pdf_path ? `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/facturas-emitidas/${j.pdf_path}` : null
        if (pdfWin) { if (pdfUrl) { pdfWin.location = pdfUrl } else { pdfWin.close() } }
        if (adjuntarDespues && row.canal === 'ml') {
          try {
            const { data: { session } } = await supabase.auth.getSession()
            const ra = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-adjuntar-factura`, {
              method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ ml_venta_id: row.id }),
            })
            const ja = await ra.json().catch(() => ({}))
            if (!(ra.ok && ja.ok)) alert(`CFE ${j.serie}-${j.numero} emitido ✓, pero no se pudo adjuntar a ML:\n${ja.error || `HTTP ${ra.status}`}${ja.ml_body ? `\nML: ${ja.ml_body}` : ''}\nUsá el botón "Adjuntar a ML".`)
          } catch (e2) { alert(`CFE emitido ✓, pero falló el adjuntar a ML: ${e2?.message || e2}. Usá "Adjuntar a ML".`) }
        }
        await cargar()
        if (!pdfUrl) alert(`CFE emitido · ${j.serie}-${j.numero}. El PDF se está generando.`)
      } else {
        if (pdfWin) pdfWin.close()
        if (j.error === 'IVA_NO_RESUELTO') setIvaError(e => ({ ...e, [row.key]: true }))
        else alert(`Error emitiendo CFE:\n${[j.error || `HTTP ${status}`, j.detalle].filter(Boolean).join('\n')}`)
      }
    } catch (e) { if (pdfWin) pdfWin.close(); alert(`Error emitiendo CFE: ${e?.message || e}`) }
    finally { setEmitiendo(null) }
  }

  function toggleDetalle(row) {
    if (expanded === row.key) { setExpanded(null); return }
    setExpanded(row.key)
    if (row.canal === 'ml' && !row.tieneCfe && !row.cancelada && !detail[row.key]) cargarPreview(row)
    else if (row.canal === 'ml' && row.tieneCfe && row.cfeId && !detail[row.key]) cargarCfeLineas(row)
  }

  // Para ventas con CFE ya emitido: trae las lineas REALES del CFE (biller_payload) con su IVA por linea,
  // en vez de reconstruir del order_items (que en packs de varios pedidos deja "Otros items del paquete").
  async function cargarCfeLineas(row) {
    try {
      const { data } = await supabase.from('facturas_emitidas').select('biller_payload, monto_total').eq('id', row.cfeId).maybeSingle()
      const bp = data?.biller_payload
      const arr = Array.isArray(bp) ? bp : (bp?.lineas || bp?.items || bp?.detalle || bp?.adenda || [])
      if (!Array.isArray(arr) || !arr.length) return
      const lineas = arr.map(l => ({
        concepto: l.concepto || l.descripcion || 'Ítem',
        cantidad: l.cantidad || 1,
        precio: Number(l.precio),
        subtotal_linea: Number(l.precio) * (l.cantidad || 1),
        indicador_facturacion: l.indicador_facturacion,
        sku: l.codigo || null,
      }))
      setDetail(d => ({ ...d, [row.key]: { ok: true, esCfe: true, lineas, monto_total: Number(data?.monto_total) || undefined } }))
    } catch (_) { /* si falla, cae al render de order_items */ }
  }
  async function asignarRepartidor(row, repId, lat, lng, dir) {
    setAsignandoRep(row.key)
    try {
      const { error } = await supabase.rpc('ml_asignar_repartidor', {
        p_ml_venta_id: row.id, p_repartidor_id: repId || null,
        p_lat: lat === '' ? null : lat, p_lng: lng === '' ? null : lng, p_direccion: dir || null,
      })
      if (error) throw error
      await cargar()
    } catch (e) {
      alert('Error al asignar repartidor: ' + e.message)
    } finally { setAsignandoRep(null) }
  }

  async function cargarPreview(row) {
    setDetailLoad(row.key)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-cfe`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ml_venta_id: row.id, dry_run: true }),
      })
      const j = await r.json().catch(() => ({}))
      setDetail(d => ({ ...d, [row.key]: j }))
    } catch (e) { setDetail(d => ({ ...d, [row.key]: { error: String(e) } })) }
    finally { setDetailLoad(null) }
  }

  async function adjuntarML(row) {
    if (!row || adjuntando) return
    const cfeTxt = row.factura ? `${row.factura.serie}-${row.factura.numero}` : 'emitida'
    if (!confirm(`Subir la factura ${cfeTxt} al pack de ML de la venta ${row.numero}?\nSe adjunta para que el comprador la descargue desde MercadoLibre.`)) return
    setAdjuntando(row.key)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-adjuntar-factura`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ml_venta_id: row.id }),
      })
      const j = await r.json().catch(() => ({}))
      if (r.ok && j.ok) { await cargar(); if (j.already) alert('La factura ya estaba adjuntada en ML.') }
      else alert(`No se pudo adjuntar a ML:\n${j.error || `HTTP ${r.status}`}${j.ml_body ? `\nML: ${j.ml_body}` : ''}`)
    } catch (e) { alert(`Error adjuntando a ML: ${e?.message || e}`) }
    finally { setAdjuntando(null) }
  }

  async function verPdf(row) {
    const path = row.factura?.pdf_path
    if (!path) { alert('Todavía no hay PDF para esta factura.'); return }
    try {
      const { data, error } = await supabase.storage.from('facturas-emitidas').createSignedUrl(path, 300)
      if (error || !data?.signedUrl) { alert('No pude abrir el PDF: ' + (error?.message || 'sin acceso al archivo')); return }
      window.open(data.signedUrl, '_blank')
    } catch (e) { alert('Error abriendo el PDF: ' + (e?.message || e)) }
  }

  // ---- multiseleccion ----
  function toggleSel(key) { setSel(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n }) }
  async function facturarSeleccionadas() {
    const items = filtradas.filter(r => sel.has(r.key) && r.facturable && r.canal === 'ml')
    if (!items.length) return alert('No hay ventas ML sin facturar entre las seleccionadas.')
    if (!confirm(`Emitir el e-Ticket de ${items.length} venta(s) ML seleccionadas?\nSe emiten en DGI vía Biller (no se puede deshacer).`)) return
    setEmitiendo('bulk')
    let ok = 0, iva = 0, err = 0
    for (const row of items) {
      try { const res = await emitir(row, false); if (res.ok) ok++; else if (res.j.error === 'IVA_NO_RESUELTO') { iva++; setIvaError(e => ({ ...e, [row.key]: true })) } else err++ }
      catch { err++ }
    }
    setEmitiendo(null); await cargar()
    alert(`Facturación masiva:\n✓ ${ok} emitidas${iva ? `\n⚠ ${iva} con IVA sin resolver (usá Forzar/Mapear)` : ''}${err ? `\n✕ ${err} con error` : ''}`)
  }
  function descargarCfes() {
    const items = filtradas.filter(r => sel.has(r.key) && r.cfePdf)
    if (!items.length) return alert('Ninguna seleccionada tiene PDF de CFE.')
    items.forEach((r, i) => setTimeout(() => window.open(r.cfePdf, '_blank'), i * 300))
  }

  function mensajeUrl(r) {
    if (r.canal === 'lp' && r.telefono) { const t = String(r.telefono).replace(/[^0-9]/g, ''); return `https://wa.me/${t.startsWith('598') ? t : '598' + t.replace(/^0/, '')}` }
    return null
  }

  const kpis = useMemo(() => {
    const mlFlexHoy = rows.filter(r => r.hoy && r.canal === 'ml' && r.esFlex && !r.cancelada).length
    const lpHoy = rows.filter(r => r.hoy && r.canal === 'lp' && !r.cancelada).length
    return {
      hoy: rows.filter(r => r.hoy && !r.cancelada).length, hoySub: `${mlFlexHoy} ML Flex · ${lpHoy} Limpack`,
      sinFacturar: rows.filter(r => r.facturable).length,
      sinAdjuntar: rows.filter(r => r.canal === 'ml' && r.tieneCfe && !r.adjuntada && !r.cancelada).length,
      cobroPend: rows.filter(r => r.canal === 'lp' && !r.pagado && !r.cancelada).length,
      canceladas: rows.filter(r => r.cancelada).length,
      bruto: rows.filter(r => !r.cancelada).reduce((s, r) => s + r.monto, 0),
    }
  }, [rows])

  // Resumen de plata que MP todavía retiene (cobrada pero no liberada) — ML + tienda.
  const libResumen = useMemo(() => {
    const ret = rows.filter(r => r.lib && r.lib.estado === 'retenido' && !r.cancelada)
    const vistos = new Set(); let monto = 0, n = 0
    for (const r of ret) {
      const k = r.lib.key || r.key
      if (vistos.has(k)) continue     // un pack con varias líneas = un solo pago retenido
      vistos.add(k); monto += (r.lib.monto || 0); n++
    }
    const prox = ret.map(r => r.lib.fecha).filter(Boolean).sort()[0] || null
    return { n, monto, prox }
  }, [rows])

  const matchVista = useCallback((r, v) => {
    switch (v) {
      case 'todas': return true
      case 'hoy': return r.hoy
      case 'pendiente': return r.accionPendiente
      case 'camino': return r.enCamino
      case 'fin': return r.finalizada
      case 'sinFacturar': return r.facturable
      case 'sinAdjuntar': return r.canal === 'ml' && r.tieneCfe && !r.adjuntada && !r.cancelada
      case 'cobroPend': return r.canal === 'lp' && !r.pagado && !r.cancelada
      case 'canceladas': return r.cancelada
      default: return true
    }
  }, [])
  const tabCount = useCallback((v) => rows.filter(r => matchVista(r, v)).length, [rows, matchVista])

  const filtradas = useMemo(() => {
    let f = rows.filter(r => matchVista(r, vista))
    if (canal === 'mlflex') f = f.filter(r => r.canal === 'ml' && r.esFlex)
    else if (canal === 'mlagencia') f = f.filter(r => r.canal === 'ml' && !r.esFlex && !r.envioDesconocido)
    else if (canal === 'lp') f = f.filter(r => r.canal === 'lp' && !r.esB2b && !r.esPet)
    else if (canal === 'petpack') f = f.filter(r => r.esPet)
    else if (canal === 'b2b') f = f.filter(r => r.esB2b)
    const s = q.trim().toLowerCase()
    if (s) f = f.filter(r => (r.numero + ' ' + r.clienteNombre + ' ' + r.clienteDoc + ' ' + r.items + ' ' + r.skus.join(' ')).toLowerCase().includes(s))
    return f
  }, [rows, vista, canal, q, matchVista])

  function exportarCsv() {
    const head = ['Canal', 'Tipo', 'Numero', 'Fecha', 'Cliente', 'Documento', 'Producto', 'Monto', 'Envio', 'EstadoEnvio', 'Factura', 'Cobro', 'Liberacion']
    const data = filtradas.map(r => [
      r.esPet ? 'PetPack' : (r.canal === 'ml' ? 'ML' : 'Limpack'), r.tipo, r.numero, new Date(r.fecha).toLocaleString('es-UY'),
      r.clienteNombre, r.clienteDoc, r.items, r.monto, r.envio, r.envioEstado,
      r.factura ? `${r.factura.serie}-${r.factura.numero}` : (r.cfeId ? 'Emitida' : (r.facturable ? 'Sin facturar' : '')), r.cobroLabel,
      r.lib ? `${r.lib.estado === 'retenido' ? 'Retenido' : 'Liberado'} ${libDateCorta(r.lib.fecha)}${r.lib.aprox ? '~' : ''}` : '',
    ])
    const csv = [head, ...data].map(fila => fila.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `ventas-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const TABS = [{ k: 'hoy', l: 'Para hoy' }, { k: 'pendiente', l: 'Acción pendiente' }, { k: 'camino', l: 'En camino' }, { k: 'fin', l: 'Finalizadas' }, { k: 'todas', l: 'Todas' }]
  const corte = corteLimpackFlex()
  const selVisibles = filtradas.filter(r => sel.has(r.key))
  const allVisSel = filtradas.length > 0 && filtradas.every(r => sel.has(r.key))

  return (
    <AdminLayout>
      <div className="vu">
        <div className="vu-head">
          <div>
            <h1>Ventas</h1>
            <p className="vu-sub">MercadoLibre y Limpack en una sola lista. <b className="vu-ml-dot">ML</b> amarillo · <b className="vu-lp-dot">Limpack</b> azul · <b className="vu-pet-dot">PetPack</b> verde.</p>
          </div>
          <div className="vu-head-acts">
            <Link className="vu-btn ghost" to="/admin/canales/mercadolibre/despacho">🚚 Despacho ML</Link>
            <button className="vu-btn ghost" onClick={exportarCsv} disabled={loading || !filtradas.length}>⭳ Excel</button>
            <button className="vu-btn ghost" onClick={() => setLimite(l => l + 250)} disabled={loading} title="Trae más ventas/pedidos viejos">{loading ? 'Cargando…' : `+ Cargar más (${rows.length})`}</button>
            <button className="vu-btn ghost" onClick={cargar} disabled={loading}>{loading ? 'Cargando…' : '↺ Actualizar'}</button>
            <Link className="vu-btn navy" to="/admin/ventas-manuales">+ Venta manual</Link>
          </div>
        </div>

        <div className="vu-strip">
          <span className={'vu-chip' + (corte.hot ? ' hot' : '')}>⏱ {corte.txt}</span>
          <span className="vu-chip">📦 Para hoy: <b>{kpis.hoy}</b> ({kpis.hoySub})</span>
          {libResumen.n > 0 && (
            <span className="vu-chip lib" title="Plata cobrada que Mercado Pago todavía retiene (ML + tienda). Se libera en la fecha estimada por MP.">🔒 Retenido en MP: <b>{fmt(libResumen.monto)}</b> · {libResumen.n} pago{libResumen.n !== 1 ? 's' : ''}{libResumen.prox ? ` · próx. libera ${libDateCorta(libResumen.prox)}` : ''}</span>
          )}
          {ruta.n > 0 && (
            <span className="vu-chip">🗺 Ruta de hoy: {ruta.barrios.slice(0, 4).join(' · ') || `${ruta.n} paradas`}{ruta.barrios.length > 4 ? ` +${ruta.barrios.length - 4}` : ''}{ruta.repart.length ? ` · ${ruta.repart.join(', ')}` : ''}</span>
          )}
        </div>

        <div className="vu-kpis">
          <div className={'vu-kpi' + (vista === 'hoy' ? ' on' : '')} onClick={() => setVista('hoy')}>
            <div className="vu-ic teal">▧</div><div><div className="vu-k-v">{kpis.hoy}</div><div className="vu-k-l">Por salir hoy</div><div className="vu-k-s">{kpis.hoySub}</div></div>
          </div>
          <div className={'vu-kpi warn' + (vista === 'sinFacturar' ? ' on' : '')} onClick={() => setVista('sinFacturar')}>
            <div className="vu-ic warn">🧾</div><div><div className="vu-k-v">{kpis.sinFacturar}</div><div className="vu-k-l">Sin facturar (ML)</div><div className="vu-k-s">Limpack se factura solo</div></div>
          </div>
          <div className={'vu-kpi' + (vista === 'sinAdjuntar' ? ' on' : '')} onClick={() => setVista('sinAdjuntar')}>
            <div className="vu-ic navy">📎</div><div><div className="vu-k-v">{kpis.sinAdjuntar}</div><div className="vu-k-l">CFE sin adjuntar a ML</div><div className="vu-k-s">emitida, falta subir</div></div>
          </div>
          <div className={'vu-kpi' + (vista === 'cobroPend' ? ' on' : '')} onClick={() => setVista('cobroPend')}>
            <div className="vu-ic sky">💳</div><div><div className="vu-k-v">{kpis.cobroPend}</div><div className="vu-k-l">Cobro pendiente</div><div className="vu-k-s">B2C + B2B</div></div>
          </div>
          <div className={'vu-kpi' + (vista === 'canceladas' ? ' on' : '')} onClick={() => setVista('canceladas')}>
            <div className="vu-ic danger">✕</div><div><div className="vu-k-v">{kpis.canceladas}</div><div className="vu-k-l">Canceladas</div><div className="vu-k-s">no despachar</div></div>
          </div>
          <div className="vu-kpi flat">
            <div className="vu-ic ok">$</div><div><div className="vu-k-v">{fmt(kpis.bruto)}</div><div className="vu-k-l">Bruto histórico</div><div className="vu-k-s">todas · sin canceladas</div></div>
          </div>
        </div>

        <div className="vu-tabsrow">
          <div className="vu-tabs">
            {TABS.map(t => (
              <button key={t.k} className={'vu-tab' + (vista === t.k ? ' on' : '')} onClick={() => setVista(t.k)}>
                {t.l}<span className="vu-tab-n">{tabCount(t.k)}</span>
              </button>
            ))}
          </div>
          <div className="vu-seg">
            {[['todos', 'Todos'], ['mlflex', 'ML Flex'], ['mlagencia', 'ML Agencia'], ['lp', 'Limpack'], ['petpack', 'PetPack'], ['b2b', 'B2B']].map(([k, l]) => (
              <button key={k} className={canal === k ? 'on' : ''} onClick={() => setCanal(k)}>{l}</button>
            ))}
          </div>
        </div>

        <div className="vu-bar">
          <label className="vu-search"><span>🔍</span>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por #venta, cliente, CI, producto o SKU" />
          </label>
          <span className="vu-count">{filtradas.length} venta{filtradas.length !== 1 ? 's' : ''} · {fmt(filtradas.filter(r => !r.cancelada).reduce((s, r) => s + r.monto, 0))} bruto</span>
        </div>

        {sel.size > 0 && (
          <div className="vu-bulk">
            <b>{selVisibles.length} seleccionada{selVisibles.length !== 1 ? 's' : ''}</b>
            <button className="vu-btn sm pri" disabled={emitiendo === 'bulk'} onClick={facturarSeleccionadas}>{emitiendo === 'bulk' ? 'Emitiendo…' : '🧾 Facturar seleccionadas (ML)'}</button>
            <button className="vu-btn sm" onClick={descargarCfes}>⭳ Descargar CFEs</button>
            <Link className="vu-btn sm" to="/admin/canales/mercadolibre/despacho">🖨 Imprimir etiquetas</Link>
            <button className="vu-btn sm ghost" onClick={() => setSel(new Set())}>Limpiar</button>
          </div>
        )}

        <div className="vu-colhead">
          <div><input type="checkbox" checked={allVisSel} onChange={() => setSel(allVisSel ? new Set() : new Set(filtradas.map(r => r.key)))} /></div>
          <div>Venta</div><div>Cliente</div><div>Productos</div><div>Envío</div><div>Factura · CFE</div><div>Cobro</div><div />
        </div>

        {loading ? (
          <div className="vu-empty">Cargando ventas…</div>
        ) : filtradas.length === 0 ? (
          <div className="vu-empty">No hay ventas para este filtro.</div>
        ) : (
          <div className="vu-list">
            {filtradas.map(r => (
              <div key={r.key} className={'vu-rowcard ' + r.canal + (r.cancelada ? ' cancel' : '') + (sel.has(r.key) ? ' sel' : '') + (expanded === r.key ? ' open' : '')}>
                <div className="vu-row">
                <div className="vu-c vu-c0"><input type="checkbox" checked={sel.has(r.key)} onChange={() => toggleSel(r.key)} /></div>
                <div className="vu-c vu-c1">
                  <div className="vu-id">{r.numero}</div>
                  <div className="vu-dt">{fechaCorta(r.fecha)}</div>
                  <div className="vu-tags">
                    <span className={'vu-ch ' + (r.esPet ? 'petpack' : r.canal)}>{r.esPet ? 'PetPack' : (r.canal === 'ml' ? 'ML' : 'Limpack')}</span>
                    <span className="vu-ch tipo">{r.tipo}</span>
                  </div>
                </div>
                <div className="vu-c vu-c2">
                  <div className="vu-cli">{r.clienteNombre}</div>
                  <div className="vu-sub">{r.clienteSub || r.clienteDoc || (r.canal === 'ml' ? 'CI s/ dato' : '')}</div>
                  {mensajeUrl(r)
                    ? <a className="vu-mini-link" href={mensajeUrl(r)} target="_blank" rel="noreferrer">💬 Mensaje al comprador</a>
                    : <Link className="vu-mini-link" to="/admin/canales/mercadolibre/ventas">💬 Mensaje</Link>}
                </div>
                <div className="vu-c vu-c3">
                  {r.skus.length > 0 && <div className="vu-skus">{r.skus.map(s => <span key={s} className="vu-sku">{s}</span>)}</div>}
                  <div className="vu-pn">{r.items}</div>
                  <div className="vu-sub">{r.itemsSub}</div>
                  <div className="vu-amt">{fmt(r.monto)}{r.envio ? <small> + envío {fmt(r.envio)}</small> : null}</div>
                </div>
                <div className="vu-c vu-c4">
                  <span className={'vu-st ' + (r.envioClass || 'sky')}><span className="vu-d" />{r.envioEstado}</span>
                  {r.canal === 'ml' && !r.cancelada && <Link className="vu-mini-link" to="/admin/canales/mercadolibre/despacho">🖨 Etiqueta</Link>}
                </div>
                <div className="vu-c vu-c5">
                  {(r.factura || r.cfeId) ? (
                    <>
                      <span className="vu-st teal">✓ Emitida{r.factura ? ` ${r.factura.serie}-${r.factura.numero}` : ''}</span>
                      {r.canal === 'lp' && <div className="vu-sub">automática</div>}
                      {r.canal === 'ml' && (r.adjuntada
                        ? <div className="vu-adj-ok">✓ Adjuntada en ML</div>
                        : <button className="vu-btn sm vu-adj" disabled={adjuntando === r.key} onClick={() => adjuntarML(r)}>{adjuntando === r.key ? 'Subiendo…' : '📎 Adjuntar a ML'}</button>)}
                      {r.factura?.pdf_path && <button className="vu-mini-link" onClick={() => verPdf(r)}>Ver PDF</button>}
                    </>
                  ) : r.noFacturaCorresponde ? (
                    <span className="vu-st grey">No corresponde</span>
                  ) : r.canal === 'lp' ? (
                    <span className="vu-st warn">Se factura sola</span>
                  ) : ivaError[r.key] ? (
                    <div className="vu-ivaerr">
                      <span className="vu-st danger">⚠ IVA sin resolver</span>
                      <div className="vu-ivaerr-acts">
                        <button className="vu-btn sm warnbtn" disabled={emitiendo === r.key} onClick={() => facturarML(r, true)}>{emitiendo === r.key ? 'Emitiendo…' : 'Forzar 22% y emitir'}</button>
                        <Link className="vu-mini-link" to="/admin/stock">Mapear producto</Link>
                      </div>
                    </div>
                  ) : r.facturable ? (
                    <div className="vu-facbtns">
                      <button className="vu-btn pri sm" disabled={emitiendo === r.key} onClick={() => facturarML(r)}>{emitiendo === r.key ? 'Emitiendo…' : '🧾 Facturar'}</button>
                      <button className="vu-mini-link asbtn" onClick={() => toggleDetalle(r)}>{expanded === r.key ? 'Cerrar' : 'Vista previa'}</button>
                    </div>
                  ) : (
                    <span className="vu-st grey">—</span>
                  )}
                </div>
                <div className="vu-c vu-c6">
                  <span className={'vu-st ' + (r.cobroClass || 'ok')}>{r.cobroLabel}</span>
                  {r.cobroSub && <div className="vu-sub">{r.cobroSub}</div>}
                  {r.lib && (
                    <span className={'vu-lib ' + r.lib.estado}
                      title={r.lib.estado === 'retenido'
                        ? `MP retiene este pago. Se libera el ${libDateLarga(r.lib.fecha)}${r.lib.monto ? ' · neto ' + fmt(r.lib.monto) : ''}`
                        : `Pago liberado por MP${r.lib.aprox ? ' (estimado, falta confirmación)' : ''} el ${libDateLarga(r.lib.fecha)}`}>
                      {r.lib.estado === 'retenido' ? '🔒 Retenido' : '🔓 Liberado'} {libDateCorta(r.lib.fecha)}{r.lib.aprox ? '~' : ''}
                    </span>
                  )}
                </div>
                <div className="vu-c vu-c7">
                  <button className="vu-detbtn" onClick={() => toggleDetalle(r)}>{expanded === r.key ? 'Cerrar ▲' : 'Detalle ▾'}</button>
                  <details className="vu-menu">
                    <summary aria-label="Más acciones">⋯</summary>
                    <div className="vu-menu-pop">
                      <button onClick={() => toggleDetalle(r)}>{expanded === r.key ? 'Cerrar detalle' : 'Ver detalle'}</button>
                      {mensajeUrl(r)
                        ? <a href={mensajeUrl(r)} target="_blank" rel="noreferrer">Mensaje (WhatsApp)</a>
                        : <Link to="/admin/canales/mercadolibre/ventas">Mensaje al comprador</Link>}
                      {r.canal === 'ml' && r.facturable && !ivaError[r.key] && <button onClick={() => toggleDetalle(r)}>Vista previa CFE</button>}
                      {r.canal === 'ml' && r.tieneCfe && !r.adjuntada && <button onClick={() => adjuntarML(r)}>Adjuntar a ML</button>}
                      {r.factura?.pdf_path && <button onClick={() => verPdf(r)}>Descargar CFE</button>}
                      {r.canal === 'ml' && !r.cancelada && <Link to="/admin/canales/mercadolibre/despacho">Etiqueta / despacho</Link>}
                    </div>
                  </details>
                </div>
                </div>
                {expanded === r.key && (
                  <div className="vu-detail">
                    <div className="vu-det-cols">
                      <div className="vu-det-box">
                        <h4><span className="vu-step">1</span>{r.canal === 'ml' ? 'Qué se factura (lo que ve el comprador en ML)' : 'Detalle del pedido'}</h4>
                        <div className="vu-det-recep">
                          <div><span>Receptor</span><b>{r.clienteNombre}</b></div>
                          <div><span>Documento</span><b>{r.clienteDoc || (r.canal === 'ml' ? 'CI s/ dato' : '—')}{r.clienteDoc && r.canal === 'ml' ? <em className="vu-tagsrc">billing_info</em> : null}</b></div>
                          <div><span>Tipo CFE</span><b>{detail[r.key]?.tipo_label || (r.canal === 'ml' ? 'e-Ticket (101)' : 'e-Ticket / e-Factura')}</b></div>
                        </div>
                        {r.direccion && <div className="vu-det-addr">📍 {r.direccion}</div>}
                        {detailLoad === r.key ? <div className="vu-sub">⏳ Calculando líneas con IVA exacto…</div>
                          : (detail[r.key]?.lineas && detail[r.key].lineas.length) ? (
                            <>
                              <table className="vu-ptable">
                                <thead><tr><th>Concepto</th><th>Cant.</th><th>IVA</th><th style={{ textAlign: 'right' }}>Importe</th></tr></thead>
                                <tbody>{detail[r.key].lineas.map((l, i) => (
                                  <tr key={i}><td>{l.concepto}{(l.sku || l.ml_item_id) && <div className="vu-linesku">{[l.sku, l.ml_item_id].filter(Boolean).join(' · ')}</div>}</td><td>{l.cantidad}</td><td><span className="vu-ivachip">{ivaLabel(l.indicador_facturacion)}</span></td><td style={{ textAlign: 'right' }}>{fmt2(l.subtotal_linea ?? (Number(l.precio) * Number(l.cantidad || 1)))}</td></tr>
                                ))}</tbody>
                              </table>
                              {detail[r.key].esCfe
                                ? <div className="vu-sub" style={{ marginTop: 6 }}>Líneas del CFE {r.factura ? `${r.factura.serie}-${r.factura.numero}` : 'emitido'} — lo que realmente se facturó.</div>
                                : <div className="vu-sub" style={{ marginTop: 6 }}>Vista previa — es exactamente lo que se emite (incluye todo el pack, con el IVA de cada producto).</div>}
                            </>
                          ) : r.canal === 'ml' ? (
                            <div className="vu-sub">Dale <b>↻ Vista previa</b> (a la derecha) para ver las líneas con el IVA exacto de cada producto del pack.</div>
                          ) : (r.lineasLocal && r.lineasLocal.length) ? (
                            <table className="vu-ptable">
                              <thead><tr><th>Concepto</th><th>Cant.</th><th>IVA</th><th style={{ textAlign: 'right' }}>Importe</th></tr></thead>
                              <tbody>{r.lineasLocal.map((l, i) => (
                                <tr key={i}><td>{l.concepto}{(l.sku || l.ml_item_id) && <div className="vu-linesku">{[l.sku, l.ml_item_id].filter(Boolean).join(' · ')}</div>}</td><td>{l.cantidad}</td><td>{l.iva_pct != null ? <span className="vu-ivachip">{l.iva_pct}%</span> : '—'}</td><td style={{ textAlign: 'right' }}>{fmt2(l.importe)}</td></tr>
                              ))}</tbody>
                            </table>
                          ) : <div className="vu-sub">{r.items} · {r.itemsSub}</div>}
                        {r.canal === 'ml' && (r.comision > 0 || r.netoReal > 0) && (
                          <div className="vu-det-cobro">
                            <div className="vu-det-cobro-t">👁 Detalle de cobro (interno, no va al CFE)</div>
                            <div>bruto <b>{fmt2(r.monto)}</b> − comisión <b>{fmt2(r.comision)}</b>{r.costoFijo > 0 ? <> − costo fijo <b>{fmt2(r.costoFijo)}</b></> : null} = <b>neto producto {fmt2(r.netoProducto)}</b></div>
                            {(r.netoReal - r.netoProducto) > 0.5
                              ? <div>+ flete/compensación que cobrás <b>{fmt2(r.netoReal - r.netoProducto)}</b> = <b>neto en caja {fmt2(r.netoReal)}</b></div>
                              : <div className="vu-det-cobro-eq">Sin flete a favor · neto en caja = neto producto ({fmt2(r.netoReal)})</div>}
                          </div>
                        )}
                      </div>
                      <div className="vu-det-box">
                        <h4><span className="vu-step">2</span>{r.canal === 'ml' ? 'Emitir y adjuntar' : 'Estado'}</h4>
                        {r.canal === 'ml' && <div className="vu-det-chargehead"><span>Monto total bruto ML</span><b>e-Ticket · Biller PROD</b></div>}
                        <div className="vu-det-charges">
                          {/* LP: r.monto = monto_total (YA incluye el envío) → Productos = monto − envío, y el Total NO vuelve a sumar el envío.
                              ML: r.monto = monto_bruto (solo producto) → Total = producto + envío. */}
                          <div><span>Productos</span><b>{fmt2(r.canal === 'lp' ? (r.monto - r.envio) : r.monto)}</b></div>
                          {r.envio ? <div><span>Envío</span><b>{fmt2(r.envio)}</b></div> : null}
                          {r.canal === 'ml' && <div><span>Descuentos</span><b>{fmt2(0)}</b></div>}
                          <div className="tot"><span>{r.canal === 'ml' ? 'Total CFE' : 'Total'}</span><b>{fmt2(detail[r.key]?.monto_total ?? (r.canal === 'lp' ? r.monto : (r.monto + r.envio)))}</b></div>
                        </div>
                        {r.canal === 'ml' && <div className="vu-det-cap">= lo que pagó el comprador · <b>se factura y sube a ML</b>. El neto que cobramos ({fmt2(r.netoReal)}) está en el detalle de cobro, a la izquierda.</div>}
                        {detail[r.key]?.ok && (() => { const g = desgloseNetoIva(detail[r.key].lineas); return <div className="vu-det-note">Neto s/IVA {fmt2(g.neto)} · IVA {fmt2(g.iva)}. Al emitir se descuenta stock (FIFO) y se libera la reserva.</div> })()}
                        {detail[r.key]?.iva_no_resuelto?.length > 0 && <div className="vu-pwarn">⚠ Hay líneas sin IVA mapeado. Forzá 22% o mapeá el producto en Stock.</div>}
                        <div className="vu-det-acts col">
                          {r.canal === 'ml' && !r.tieneCfe && !r.cancelada ? (
                            detail[r.key]?.iva_no_resuelto?.length > 0 ? (
                              <>
                                <button className="vu-btn warnbtn full" disabled={emitiendo === r.key} onClick={() => facturarML(r, true, true)}>{emitiendo === r.key ? 'Emitiendo…' : 'Forzar 22%: emitir y adjuntar a ML'}</button>
                                <button className="vu-btn full" disabled={emitiendo === r.key} onClick={() => facturarML(r, true, false)}>Forzar 22%: solo emitir</button>
                              </>
                            ) : (
                              <>
                                <button className="vu-btn pri full" disabled={emitiendo === r.key || detailLoad === r.key} onClick={() => facturarML(r, false, true)}>{emitiendo === r.key ? 'Emitiendo…' : '🧾 Emitir e-Ticket y adjuntar a ML'}</button>
                                <div className="vu-det-btnrow">
                                  <button className="vu-btn" disabled={emitiendo === r.key} onClick={() => facturarML(r, false, false)}>Solo emitir</button>
                                  <button className="vu-btn" disabled={detailLoad === r.key} onClick={() => cargarPreview(r)}>↻ Vista previa</button>
                                </div>
                              </>
                            )
                          ) : (
                            <div className="vu-det-btnrow">
                              {r.canal === 'ml' && r.tieneCfe && !r.adjuntada && <button className="vu-btn vu-adj" disabled={adjuntando === r.key} onClick={() => adjuntarML(r)}>{adjuntando === r.key ? 'Subiendo…' : '📎 Adjuntar a ML'}</button>}
                              {r.adjuntada && <span className="vu-adj-ok">✓ Adjuntada en ML</span>}
                              {r.canal === 'lp' && !r.noFacturaCorresponde && <span className="vu-st warn">Se factura sola</span>}
                              {r.factura?.pdf_path && <button className="vu-btn ghost" onClick={() => verPdf(r)}>Ver PDF</button>}
                              <Link className="vu-btn ghost" to={r.canal === 'ml' ? '/admin/canales/mercadolibre/ventas' : '/admin/pedidos'}>Ficha completa ↗</Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {r.canal === 'ml' && !r.cancelada && <RepartoBlock row={r} reps={posiblesReps} asignando={asignandoRep === r.key} onAsignar={asignarRepartidor} />}
                    {r.canal === 'ml' && !r.tieneCfe && <div className="vu-det-fiscal">📎 El PDF se sube solo a <b>packs/{r.packId || '{pack}'}/fiscal_documents</b>. Si ML rechaza la subida, el CFE igual queda emitido y la venta pasa a <b>Falta adjuntar</b> con el PDF listo para bajar.</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="vu-foot">
          Todo junto: ML (amarillo) y Limpack (azul). Seleccioná varias para facturar o bajar CFEs en lote.
          <b> Facturar</b> emite el e-Ticket (Biller); si el ítem no tiene IVA mapeado ofrece Forzar 22% o Mapear.
          <b> Adjuntar a ML</b> sube el PDF al pack. Limpack se factura solo.
        </p>
      </div>

      <style>{`
        .vu{ font-family:'Inter',system-ui,sans-serif; color:#1A3461; max-width:1520px; margin:0 auto;
          --navy:#1A3461; --teal:#00A8B5; --ok:#15803D; --ok-bg:#DCFCE7; --warn:#B45309; --warn-bg:#FEF3C7;
          --danger:#B91C1C; --danger-bg:#FEE2E2; --sky-bg:#E8F6FB; --sky-ink:#1E6F8A; --grey-bg:#F1F5F9; --grey-ink:#64748B;
          --teal-bg:#E0F7F9; --teal-ink:#00707A; --navy-bg:#E8EDF5;
          --ml:#FFE600; --ml-stripe:#F0B400; --ml-wash:#FFFBEA; --lp:#1A3461; --lp-wash:#EDF2FB;
          --line:#E6EDF6; --num:'Manrope','Inter',system-ui,sans-serif; --sh:0 1px 2px rgba(15,31,61,.05), 0 6px 16px rgba(15,31,61,.07); }
        .vu-head{ display:flex; justify-content:space-between; align-items:flex-end; gap:12px; margin-bottom:12px; flex-wrap:wrap }
        .vu-head h1{ font-family:var(--num); font-size:26px; font-weight:800; letter-spacing:-.02em; margin:0 }
        .vu-head-acts{ display:flex; gap:8px; flex-wrap:wrap; align-items:center }
        .vu-btn.navy{ background:var(--navy); border-color:var(--navy); color:#fff } .vu-btn.navy:hover{ background:#12294d }
        .vu-sub{ font-size:13px; color:#64748B; margin:4px 0 0 }
        .vu-ml-dot{ background:var(--ml); color:#3A3000; padding:1px 6px; border-radius:5px; font-weight:700 }
        .vu-lp-dot{ background:var(--lp); color:#fff; padding:1px 6px; border-radius:5px; font-weight:700 }
        .vu-strip{ display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px }
        .vu-chip{ display:inline-flex; align-items:center; gap:7px; background:#fff; border:1px solid var(--line); border-radius:999px; padding:7px 14px; font-size:12.5px; color:var(--navy); box-shadow:var(--sh) }
        .vu-chip.hot{ border-color:#FDBA74; background:#FFF7ED; color:#9A3412 }
        .vu-chip b{ font-family:var(--num) }

        .vu-kpis{ display:grid; grid-template-columns:repeat(auto-fit,minmax(184px,1fr)); gap:12px; margin-bottom:18px }
        .vu-kpi{ display:flex; align-items:flex-start; gap:11px; background:#fff; border:1px solid var(--line); border-radius:14px; padding:13px 15px; box-shadow:var(--sh); cursor:pointer; transition:box-shadow .15s, border-color .15s }
        .vu-kpi.flat{ cursor:default }
        .vu-kpi:not(.flat):hover{ box-shadow:0 8px 22px rgba(15,31,61,.12) }
        .vu-kpi.on{ border-color:var(--teal); box-shadow:0 0 0 3px rgba(0,168,181,.14), var(--sh) }
        .vu-kpi.warn.on{ border-color:var(--warn); box-shadow:0 0 0 3px rgba(180,83,9,.14), var(--sh) }
        .vu-ic{ width:34px; height:34px; border-radius:10px; display:grid; place-items:center; font-size:15px; flex:none }
        .vu-ic.teal{ background:var(--teal-bg); color:var(--teal-ink) } .vu-ic.warn{ background:var(--warn-bg); color:var(--warn) }
        .vu-ic.navy{ background:var(--navy-bg); color:var(--navy) } .vu-ic.sky{ background:var(--sky-bg); color:var(--sky-ink) }
        .vu-ic.danger{ background:var(--danger-bg); color:var(--danger) } .vu-ic.ok{ background:var(--ok-bg); color:var(--ok); font-family:var(--num); font-weight:800 }
        .vu-k-v{ font-family:var(--num); font-size:21px; font-weight:800; letter-spacing:-.02em; color:var(--navy); line-height:1.05 }
        .vu-kpi.warn .vu-k-v{ color:var(--warn) }
        .vu-k-l{ font-size:11px; color:#475569; font-weight:700; margin-top:2px }
        .vu-k-s{ font-size:10.5px; color:#94A3B8; margin-top:2px }

        .vu-tabsrow{ display:flex; justify-content:space-between; align-items:center; gap:12px; border-bottom:1px solid var(--line); margin-bottom:12px; flex-wrap:wrap }
        .vu-tabs{ display:flex; gap:2px; flex-wrap:wrap }
        .vu-tab{ background:none; border:0; border-bottom:2px solid transparent; padding:9px 12px; font-size:13.5px; font-weight:600; color:#64748B; cursor:pointer; font-family:inherit; display:flex; align-items:center; gap:7px }
        .vu-tab.on{ color:var(--navy); border-bottom-color:var(--teal) }
        .vu-tab-n{ font-family:var(--num); font-size:11px; font-weight:700; background:var(--navy-bg); color:var(--navy); border-radius:999px; padding:1px 7px }
        .vu-tab.on .vu-tab-n{ background:var(--teal); color:#fff }
        .vu-seg{ display:inline-flex; background:#EEF2F8; border-radius:10px; padding:3px }
        .vu-seg button{ border:0; background:none; padding:6px 12px; font-size:12.5px; font-weight:700; color:#64748B; cursor:pointer; border-radius:8px; font-family:inherit }
        .vu-seg button.on{ background:var(--navy); color:#fff }

        .vu-bar{ display:flex; align-items:center; gap:12px; margin-bottom:10px; flex-wrap:wrap }
        .vu-search{ flex:1; min-width:240px; display:flex; align-items:center; gap:8px; background:#fff; border:1px solid var(--line); border-radius:10px; padding:0 12px; height:40px }
        .vu-search input{ border:0; outline:0; flex:1; font-size:13.5px; font-family:inherit; background:transparent; color:var(--navy) }
        .vu-count{ font-size:13px; color:#64748B; font-weight:600 }

        .vu-bulk{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; background:var(--navy-bg); border:1px solid #D5DEEC; border-radius:12px; padding:10px 14px; margin-bottom:12px }
        .vu-bulk > b{ font-size:13px; color:var(--navy); margin-right:4px }

        .vu-colhead{ display:grid; grid-template-columns:34px 148px 1.05fr 1.4fr .82fr 1.25fr .92fr 88px; gap:12px; padding:0 16px 6px 18px; font-size:10.5px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:#94A3B8; align-items:center }
        @media (max-width:1160px){ .vu-colhead{ display:none } }
        .vu-empty{ background:#fff; border:1px solid var(--line); border-radius:14px; padding:40px; text-align:center; color:#64748B; box-shadow:var(--sh) }
        .vu-list{ display:flex; flex-direction:column; gap:10px }
        .vu-rowcard{ position:relative; background:#fff; border:1px solid var(--line); border-radius:14px; box-shadow:var(--sh) }
        .vu-rowcard.ml{ background:linear-gradient(90deg,var(--ml-wash),#fff 260px) }
        .vu-rowcard.lp{ background:linear-gradient(90deg,var(--lp-wash),#fff 260px) }
        .vu-rowcard.sel{ border-color:var(--teal); box-shadow:0 0 0 2px rgba(0,168,181,.16), var(--sh) }
        .vu-rowcard.open{ border-color:var(--teal); box-shadow:0 0 0 3px rgba(0,168,181,.12), var(--sh) }
        .vu-rowcard::before{ content:""; position:absolute; left:0; top:12px; bottom:12px; width:4px; border-radius:0 4px 4px 0; z-index:1 }
        .vu-rowcard.ml::before{ background:var(--ml-stripe) }
        .vu-rowcard.lp::before{ background:var(--lp) }
        .vu-rowcard.cancel{ opacity:.6 }
        .vu-row{ position:relative; display:grid; grid-template-columns:34px 148px 1.05fr 1.4fr .82fr 1.25fr .92fr 88px; gap:14px; align-items:start; padding:15px 16px 15px 20px }
        .vu-c2, .vu-c4, .vu-c5, .vu-c6{ display:flex; flex-direction:column; align-items:flex-start; gap:6px }
        .vu-c2 > *, .vu-c4 > *, .vu-c5 > *, .vu-c6 > *{ margin-top:0 }
        .vu-c0{ padding-top:2px }
        .vu-facbtns, .vu-ivaerr-acts{ gap:5px }
        .vu-detail{ border-top:1px dashed var(--line); padding:16px 18px; }
        .vu-det-cols{ display:grid; grid-template-columns:1.35fr 1fr; gap:16px }
        @media (max-width:900px){ .vu-det-cols{ grid-template-columns:1fr } }
        .vu-det-box{ background:#fff; border:1px solid var(--line); border-radius:12px; padding:14px }
        .vu-det-box h4{ margin:0 0 12px; font-size:13px; color:var(--navy); font-weight:800; display:flex; align-items:center }
        .vu-step{ display:inline-grid; place-items:center; width:20px; height:20px; border-radius:50%; background:var(--teal); color:#fff; font-family:var(--num); font-size:11px; font-weight:800; margin-right:9px; flex:none }
        .vu-tagsrc{ font-style:normal; font-size:10px; font-weight:700; background:#DCFCE7; color:#166534; border-radius:5px; padding:1px 6px; margin-left:6px }
        .vu-det-chargehead{ display:flex; justify-content:space-between; align-items:center; font-size:11px; color:#94A3B8; padding-bottom:8px; margin-bottom:8px; border-bottom:1px solid var(--line) }
        .vu-det-chargehead b{ color:var(--navy); font-family:var(--num); font-weight:700 }
        .vu-linesku{ font-size:10px; color:#94A3B8; font-family:var(--num); margin-top:2px }
        .vu-det-acts.col{ flex-direction:column; align-items:stretch; gap:8px }
        .vu-btn.full{ width:100%; justify-content:center; padding:12px }
        .vu-det-btnrow{ display:flex; gap:8px; flex-wrap:wrap }
        .vu-det-cap{ font-size:11px; color:#475569; margin-top:8px; background:#F5F8FC; border-radius:8px; padding:8px 10px; line-height:1.5 }
        .vu-det-fiscal{ background:#EAF6FE; border:1px solid #CDE8FA; color:#1E5A85; border-radius:10px; padding:11px 14px; font-size:11.5px; margin-top:14px; line-height:1.5 }
        .vu-det-reparto{ margin-top:14px; background:#F0F9FF; border:1px solid #BAE6FD; border-radius:12px; padding:12px 14px }
        .vu-det-reparto-h{ font-size:11px; font-weight:800; color:#0369A1; text-transform:uppercase; letter-spacing:.05em; margin-bottom:8px; display:flex; align-items:center; gap:8px; flex-wrap:wrap }
        .vu-rep-actual{ font-size:10.5px; font-weight:700; background:#E0E7FF; color:#3730A3; border-radius:999px; padding:2px 8px; text-transform:none; letter-spacing:0 }
        .vu-rep-row{ display:flex; gap:8px; margin-bottom:8px; flex-wrap:wrap }
        .vu-rep-row select{ flex:1; min-width:160px; padding:7px 10px; border:1px solid var(--line); border-radius:8px; font-size:12.5px; font-family:inherit; background:#fff }
        .vu-rep-input{ width:100%; padding:7px 10px; border:1px solid var(--line); border-radius:8px; font-size:12.5px; font-family:inherit; margin-bottom:8px; box-sizing:border-box }
        .vu-det-fiscal b{ color:#0F3D5E }
        .vu-c7{ display:flex; flex-direction:column; align-items:flex-end; gap:5px }
        .vu-detbtn{ border:1px solid var(--line); background:#fff; border-radius:8px; padding:5px 10px; font-size:12px; font-weight:700; color:var(--teal-ink); cursor:pointer; font-family:inherit; white-space:nowrap }
        .vu-detbtn:hover{ background:var(--teal-bg); border-color:var(--teal) }
        .vu-det-recep{ display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:12px }
        .vu-det-recep span{ display:block; font-size:9.5px; text-transform:uppercase; letter-spacing:.05em; color:#94A3B8; margin-bottom:2px }
        .vu-det-recep b{ font-size:12.5px; color:#0F172A }
        .vu-det-charges{ font-size:13px }
        .vu-det-charges > div{ display:flex; justify-content:space-between; padding:4px 0; color:#64748B }
        .vu-det-charges b{ font-family:var(--num); color:var(--navy) }
        .vu-det-charges .tot{ border-top:1.5px solid var(--line); margin-top:4px; padding-top:8px; font-weight:700; color:var(--navy) }
        .vu-det-charges .tot b{ font-size:16px }
        .vu-det-acts{ display:flex; flex-wrap:wrap; gap:8px; margin-top:12px; align-items:center }
        .vu-det-addr{ font-size:12px; color:#475569; background:#F5F8FC; border-radius:8px; padding:7px 10px; margin-bottom:12px }
        .vu-det-cobro{ font-size:11.5px; color:#64748B; margin-top:12px; padding-top:10px; border-top:1px dashed var(--line); line-height:1.7 }
        .vu-det-cobro b{ color:var(--navy); font-family:var(--num) }
        .vu-det-cobro-t{ font-weight:700; color:#475569; margin-bottom:2px }
        .vu-det-cobro-eq{ color:#94A3B8 }
        .vu-det-note{ font-size:11.5px; color:#64748B; margin-top:10px; line-height:1.5 }
        .vu-det-note.sm{ font-size:11px; color:#94A3B8; background:#F5F8FC; border-radius:8px; padding:8px 10px }
        .vu-c0 input, .vu-colhead input{ width:16px; height:16px; accent-color:var(--teal); cursor:pointer }
        .vu-id{ font-family:var(--num); font-weight:700; font-size:13px; color:var(--navy) }
        .vu-dt{ font-size:11px; color:#64748B; margin-top:1px }
        .vu-tags{ display:flex; gap:5px; margin-top:6px; flex-wrap:wrap }
        .vu-ch{ display:inline-flex; align-items:center; height:20px; padding:0 7px; border-radius:6px; font-size:10.5px; font-weight:700 }
        .vu-ch.ml{ background:var(--ml); color:#3A3000 } .vu-ch.lp{ background:var(--lp); color:#fff }
        .vu-ch.petpack{ background:#E9F7EC; color:#1F8A3C }
        .vu-pet-dot{ background:#E9F7EC; color:#1F8A3C; padding:1px 6px; border-radius:5px; font-weight:700 }
        .vu-ch.tipo{ background:#F1F5F9; color:#334155; border:1px solid var(--line) }
        .vu-cli{ font-weight:700; font-size:13px; color:#0F172A }
        .vu-sub{ font-size:11px; color:#64748B; margin-top:2px }
        .vu-skus{ display:flex; gap:4px; flex-wrap:wrap; margin-bottom:4px }
        .vu-sku{ font-family:var(--num); font-size:10px; font-weight:700; background:#E8EDF5; color:#334155; border-radius:5px; padding:1px 5px }
        .vu-pn{ font-size:12.5px; color:#0F172A; font-weight:600; line-height:1.3; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden }
        .vu-amt{ font-family:var(--num); font-weight:800; font-size:13.5px; color:var(--navy); margin-top:4px }
        .vu-amt small{ font-weight:600; color:#64748B; font-size:11px }
        .vu-st{ display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:700; padding:3px 9px; border-radius:8px; white-space:nowrap }
        .vu-st .vu-d{ width:7px; height:7px; border-radius:50% }
        .vu-st.ok{ background:var(--ok-bg); color:var(--ok) } .vu-st.ok .vu-d{ background:var(--ok) }
        .vu-st.warn{ background:var(--warn-bg); color:var(--warn) } .vu-st.warn .vu-d{ background:var(--warn) }
        .vu-st.sky{ background:var(--sky-bg); color:var(--sky-ink) } .vu-st.sky .vu-d{ background:var(--sky-ink) }
        .vu-st.teal{ background:var(--teal-bg); color:var(--teal-ink) } .vu-st.teal .vu-d{ background:var(--teal-ink) }
        .vu-st.grey{ background:var(--grey-bg); color:var(--grey-ink) } .vu-st.grey .vu-d{ background:var(--grey-ink) }
        .vu-st.danger{ background:var(--danger-bg); color:var(--danger) } .vu-st.danger .vu-d{ background:var(--danger) }
        .vu-mini-link{ font-size:11.5px; color:var(--teal-ink); font-weight:600; text-decoration:none; display:inline-block; margin-top:4px; background:none; border:0; cursor:pointer; font-family:inherit; padding:0 }
        .vu-mini-link:hover{ text-decoration:underline }
        .vu-mini-link.asbtn{ margin-top:0 }
        .vu-facbtns{ display:flex; flex-direction:column; align-items:flex-start; gap:3px }
        .vu-adj-ok{ font-size:12px; font-weight:700; color:#15803D; margin-top:5px }
        .vu-lib{ display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:800; padding:2px 7px; border-radius:6px; white-space:nowrap; margin-top:1px }
        .vu-lib.retenido{ background:var(--warn-bg); color:var(--warn) }
        .vu-lib.liberado{ background:var(--ok-bg); color:var(--ok) }
        .vu-chip.lib{ border-color:#FDBA74; background:#FFF7ED; color:#9A3412 }
        .vu-chip.lib b{ color:#9A3412 }
        .vu-ivaerr-acts{ display:flex; flex-direction:column; align-items:flex-start; gap:2px; margin-top:5px }
        .vu-btn{ border:1px solid var(--line); background:#fff; border-radius:9px; padding:8px 12px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; color:var(--navy); text-decoration:none; display:inline-flex; align-items:center; gap:6px }
        .vu-btn.ghost:hover{ background:#F1F5F9 }
        .vu-btn.pri{ background:var(--teal); border-color:var(--teal); color:#fff } .vu-btn.pri:hover{ background:#008E99 }
        .vu-btn.sm{ padding:6px 10px; font-size:12px }
        .vu-btn.vu-adj{ margin-top:5px; border-color:#F0B400; background:#FFFBEA; color:#8A6D00 } .vu-btn.vu-adj:hover{ background:#FFF3C4 }
        .vu-btn.warnbtn{ border-color:var(--warn); background:var(--warn-bg); color:var(--warn) } .vu-btn.warnbtn:hover{ background:#FDE9C8 }
        .vu-btn:disabled{ opacity:.6; cursor:wait }
        .vu-menu{ position:relative; display:inline-block }
        .vu-menu summary{ list-style:none; width:30px; height:30px; border-radius:8px; display:grid; place-items:center; cursor:pointer; color:#64748B; font-size:18px; font-weight:800; user-select:none }
        .vu-menu summary::-webkit-details-marker{ display:none }
        .vu-menu summary:hover{ background:#F1F5F9; color:var(--navy) }
        .vu-menu[open] summary{ background:var(--navy-bg); color:var(--navy) }
        .vu-menu-pop{ position:absolute; right:0; top:34px; z-index:30; background:#fff; border:1px solid var(--line); border-radius:12px; box-shadow:0 12px 30px rgba(15,31,61,.16); padding:5px; min-width:190px; display:flex; flex-direction:column }
        .vu-menu-pop > a, .vu-menu-pop > button{ text-align:left; background:none; border:0; padding:8px 11px; border-radius:8px; font-size:13px; font-weight:600; color:#334155; cursor:pointer; font-family:inherit; text-decoration:none; white-space:nowrap }
        .vu-menu-pop > a:hover, .vu-menu-pop > button:hover{ background:var(--teal-bg); color:var(--teal-ink) }
        .vu-foot{ font-size:12px; color:#64748B; margin-top:18px; padding-top:12px; border-top:1px solid var(--line) }

        .vu-modal-bg{ position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; z-index:100; padding:20px }
        .vu-modal{ background:#fff; border-radius:16px; width:100%; max-width:560px; box-shadow:0 24px 60px rgba(15,31,61,.28); padding:20px; max-height:90vh; overflow:auto }
        .vu-modal-head{ display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:14px }
        .vu-modal-head b{ font-family:var(--num); font-size:16px; color:var(--navy) }
        .vu-x{ border:0; background:#F1F5F9; width:30px; height:30px; border-radius:8px; cursor:pointer; color:#64748B; font-size:14px }
        .vu-ptable{ width:100%; border-collapse:collapse; font-size:13px }
        .vu-ptable th{ text-align:left; font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; color:#94A3B8; padding:6px 8px; border-bottom:1px solid var(--line) }
        .vu-ptable td{ padding:8px; border-bottom:1px solid #F1F5F9; color:#0F172A }
        .vu-ivachip{ font-family:var(--num); font-size:11px; font-weight:700; background:var(--teal-bg); color:var(--teal-ink); border-radius:5px; padding:1px 6px }
        .vu-ptotal{ display:flex; justify-content:space-between; align-items:center; margin-top:12px; padding-top:10px; border-top:1.5px solid var(--line); font-size:13px; color:#64748B }
        .vu-ptotal b{ font-family:var(--num); font-size:18px; color:var(--navy) }
        .vu-pwarn{ background:var(--warn-bg); color:var(--warn); border-radius:10px; padding:10px 12px; font-size:12.5px; margin-top:12px; font-weight:600 }
        .vu-modal-acts{ display:flex; justify-content:flex-end; gap:8px; margin-top:16px }

        @media (max-width:1160px){ .vu-row{ grid-template-columns:34px 1fr 1fr; gap:8px 12px } }
        @media (max-width:640px){ .vu-row{ grid-template-columns:1fr } .vu-c0{ display:none } }
      `}</style>
    </AdminLayout>
  )
}
